import { FC } from 'react';

import {
  DELIVERY_CHANNEL_CONFIG,
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Image } from '@shared/api/media/media.interface';

import {
  formatDate,
  formatDateTime,
  formatDateTimeSec,
  formatPhone,
  getChoiceUnitName,
  getImageUrl,
  getItemName,
  getOrdererInfo,
  getStatusBadge,
} from '../../utils/couponSearch.utils';

export interface VerificationData {
  verificationId: string;
  phoneNumber: string;
  code: string;
  verifiedAt: Date;
}

interface CouponTableRowProps {
  coupon: GiftCouponForRedemption;
  isNewDeliveredGroup: boolean;

  // 선택 (미전달 처리용)
  isSelected: boolean;
  onToggleSelect: (id: string) => void;

  // 메모/확인번호 편집
  isEditing: boolean;
  editPhone: string;
  editMemo: string;
  isSavingEdit: boolean;
  onSetEditPhone: (v: string) => void;
  onSetEditMemo: (v: string) => void;
  onStartEdit: (coupon: GiftCouponForRedemption) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;

  // 발송일시 편집
  isEditingDeliveredAt: boolean;
  editDeliveredAt: string;
  isSavingDeliveredAt: boolean;
  onSetEditDeliveredAt: (v: string) => void;
  onStartEditDeliveredAt: (coupon: GiftCouponForRedemption) => void;
  onSaveEditDeliveredAt: () => void;
  onCancelEditDeliveredAt: () => void;

  // 발송매체
  channelEditMode: boolean;
  isChannelSelected: boolean;
  deliveredGroupMap: Map<string, string[]>;
  channelSelectedIds: Set<string>;
  onToggleChannelOne: (id: string) => void;
  onToggleChannelGroup: (groupKey: string) => void;

  // 재발행 폐기
  isDeletingThis: boolean;
  onDeleteAdditional: (coupon: GiftCouponForRedemption) => void;

  // issue (환불/연장 링크)
  isIssuing?: boolean;
  onIssueRefund?: (couponId: string) => Promise<void>;
  onIssueRenewal?: (couponId: string) => Promise<void>;
  onCancelIssue?: (couponId: string) => Promise<void>;

  // 서명 보기
  onOpenSignatureModal: (signatureImage: Image) => void;

  // 인증 정보 보기
  onOpenVerificationModal: (verification: VerificationData) => void;

  // 복사
  onCopyToClipboard: (text: string) => void;
}

export const CouponTableRow: FC<CouponTableRowProps> = ({
  coupon,
  isNewDeliveredGroup,
  isSelected,
  onToggleSelect,
  isEditing,
  editPhone,
  editMemo,
  isSavingEdit,
  onSetEditPhone,
  onSetEditMemo,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  isEditingDeliveredAt,
  editDeliveredAt,
  isSavingDeliveredAt,
  onSetEditDeliveredAt,
  onStartEditDeliveredAt,
  onSaveEditDeliveredAt,
  onCancelEditDeliveredAt,
  channelEditMode,
  isChannelSelected,
  deliveredGroupMap,
  channelSelectedIds,
  onToggleChannelOne,
  onToggleChannelGroup,
  isDeletingThis,
  onDeleteAdditional,
  isIssuing,
  onIssueRefund,
  onIssueRenewal,
  onCancelIssue,
  onOpenSignatureModal,
  onOpenVerificationModal,
  onCopyToClipboard: _onCopyToClipboard,
}) => {
  void _onCopyToClipboard;
  const badge = getStatusBadge(coupon.redeemStatus);
  const choiceUnitName = getChoiceUnitName(coupon);
  const ordererInfo = getOrdererInfo(coupon);
  const imageUrl = getImageUrl(coupon);

  return (
    <tr
      className={`${
        coupon.redeemStatus === GiftCouponRedeemStatus.Disposed
          ? 'bg-gray-50 opacity-60'
          : channelEditMode && isChannelSelected
          ? 'bg-blue-50/40'
          : coupon.isAdditional || coupon.isReissued
          ? 'bg-amber-50/40 hover:bg-amber-50'
          : 'hover:bg-gray-50'
      } ${isNewDeliveredGroup ? 'border-t-2 !border-t-red-400' : ''}`}
    >
      {/* 체크박스 */}
      <td className="whitespace-nowrap px-2 py-3 text-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(coupon._id)}
          disabled={coupon.redeemStatus === GiftCouponRedeemStatus.Delivered}
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-30"
        />
      </td>

      {/* 상태 */}
      <td className="whitespace-nowrap px-4 py-3">
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badge.className}`}
        >
          {badge.text}
        </span>
        {coupon.redeemStatus === GiftCouponRedeemStatus.Disposed &&
          coupon.disposalReason && (
            <p
              className="mt-0.5 max-w-[100px] truncate text-[9px] text-slate-400"
              title={coupon.disposalReason}
            >
              {coupon.disposalReason}
            </p>
          )}
      </td>

      {/* 공유 */}
      <td className="whitespace-nowrap px-4 py-3">
        {coupon.shareStatus === GiftCouponShareStatus.NotShared ? (
          <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-500">
            미전달
          </span>
        ) : coupon.shareStatus === GiftCouponShareStatus.Shared ? (
          <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-700">
            공유됨
          </span>
        ) : coupon.shareStatus === GiftCouponShareStatus.Viewed ? (
          <span className="inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700">
            조회됨
          </span>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>

      {/* 쿠폰번호 */}
      <td className="whitespace-nowrap px-4 py-3">
        <div className="flex items-center gap-1.5">
          {coupon.isReissued && (
            <span className="inline-flex items-center rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold text-teal-700">
              재발급
            </span>
          )}
          {coupon.isAdditional && (
            <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
              재발행
            </span>
          )}
          {coupon.isRenewal && (
            <span className="inline-flex items-center rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold text-violet-700">
              연장
            </span>
          )}
          {coupon.isRenewed && (
            <span className="inline-flex items-center rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold text-violet-500">
              연장발급됨
            </span>
          )}
          <span className="font-mono text-sm text-gray-700">
            {(coupon.fullCode && coupon.fullCode.replace(/-undefined/g, '').slice(0)) ||
              '-'}
          </span>
        </div>
        <a
          href={`${window.location.origin}/gift-store/coupon/${
            coupon.code
          }?secretKey=${encodeURIComponent(
            process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY ?? '',
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-0.5 font-mono text-[10px] text-gray-400 hover:text-indigo-600"
          title="클릭하여 쿠폰 미리보기 (새 탭)"
        >
          {coupon._id.slice(-6)}
        </a>
      </td>

      {/* 상품 */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {imageUrl && (
            <img src={imageUrl} alt="" className="h-8 w-8 rounded object-cover" />
          )}
          <div>
            <p className="text-sm font-medium text-gray-900">{getItemName(coupon)}</p>
            {choiceUnitName && (
              <p className="max-w-[180px] truncate text-xs text-indigo-600">
                {choiceUnitName}
              </p>
            )}
          </div>
        </div>
      </td>

      {/* 주문자 */}
      <td className="whitespace-nowrap px-4 py-3">
        <p className="text-sm text-gray-900">{ordererInfo.name}</p>
        <p className="text-xs text-gray-500">{formatPhone(ordererInfo.phone)}</p>
      </td>

      {/* 구매일 */}
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
        {formatDate(ordererInfo.purchasedAt)}
      </td>

      {/* 메모 */}
      <td className="max-w-[150px] px-4 py-3">
        {isEditing ? (
          <input
            type="text"
            value={editMemo}
            onChange={(e) => onSetEditMemo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveEdit();
              if (e.key === 'Escape') onCancelEdit();
            }}
            disabled={isSavingEdit}
            placeholder="메모"
            className="w-full rounded border border-indigo-300 px-1.5 py-0.5 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        ) : (
          <button
            onClick={() => onStartEdit(coupon)}
            className="group flex items-center gap-1 text-left"
            title="클릭하여 메모/확인번호 수정"
          >
            {coupon.memo ? (
              <p className="truncate text-xs text-gray-600" title={coupon.memo}>
                {coupon.memo}
              </p>
            ) : (
              <span className="text-[10px] text-gray-300">-</span>
            )}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 shrink-0 text-gray-300 group-hover:text-indigo-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
      </td>

      {/* 수신자 */}
      <td className="whitespace-nowrap px-4 py-3">
        <div className="flex items-center gap-1.5">
          <p className="text-sm text-gray-900">
            {formatPhone(coupon.recipient?.phoneNumber)}
          </p>
          {coupon.verification &&
            (() => {
              const verifiedPhone = coupon.verification.phoneNumber.replace(
                /[^0-9]/g,
                '',
              );
              const recipientPhone =
                coupon.recipient?.phoneNumber?.replace(/[^0-9]/g, '') || '';
              const isPhoneMismatch =
                verifiedPhone && recipientPhone && verifiedPhone !== recipientPhone;

              return (
                <button
                  onClick={() => {
                    if (coupon.verification) onOpenVerificationModal(coupon.verification);
                  }}
                  className={`inline-flex items-center rounded-full p-1 ${
                    isPhoneMismatch
                      ? 'bg-red-100 text-red-700 ring-1 ring-red-300 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title={
                    isPhoneMismatch
                      ? `인증 번호 불일치! 인증: ${verifiedPhone} ≠ 수신자: ${recipientPhone}`
                      : `본인 인증 완료 (${new Date(
                          coupon.verification.verifiedAt,
                        ).toLocaleString('ko-KR')})`
                  }
                >
                  {isPhoneMismatch ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0010 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              );
            })()}
          {coupon.recipient?.signatureImage && (
            <button
              onClick={() => {
                const sig = coupon.recipient?.signatureImage;
                if (sig) onOpenSignatureModal(sig);
              }}
              className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-200"
              title="클릭하여 서명 확인"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              서명
            </button>
          )}
        </div>
        {coupon.recipient?.name && (
          <p className="text-xs text-gray-500">{coupon.recipient.name}</p>
        )}
        {isEditing ? (
          <div className="mt-1 flex items-center gap-1">
            <input
              type="tel"
              value={editPhone}
              onChange={(e) => onSetEditPhone(e.target.value.replace(/[^0-9]/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveEdit();
                if (e.key === 'Escape') onCancelEdit();
              }}
              disabled={isSavingEdit}
              placeholder="확인번호"
              maxLength={11}
              className="w-[100px] rounded border border-amber-300 px-1.5 py-0.5 text-xs focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            <button
              onClick={onSaveEdit}
              disabled={isSavingEdit}
              className="rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSavingEdit ? '...' : '저장'}
            </button>
            <button
              onClick={onCancelEdit}
              disabled={isSavingEdit}
              className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-300 disabled:opacity-50"
            >
              취소
            </button>
          </div>
        ) : (
          coupon.additionalRecipientPhone && (
            <p className="text-xs text-amber-600">
              {formatPhone(coupon.additionalRecipientPhone)}
            </p>
          )
        )}
      </td>

      {/* 신청일시 */}
      <td className="whitespace-nowrap px-4 py-3">
        <div className="text-sm text-gray-700">
          {formatDateTime(coupon.recipient?.requestedAt)}
        </div>
        <div className="text-[11px] text-gray-400">{formatDate(coupon.expiresAt)}</div>
      </td>

      {/* 발송일시 */}
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered ? (
          isEditingDeliveredAt ? (
            <div className="flex flex-col gap-1">
              <input
                type="datetime-local"
                value={editDeliveredAt}
                onChange={(e) => onSetEditDeliveredAt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveEditDeliveredAt();
                  if (e.key === 'Escape') onCancelEditDeliveredAt();
                }}
                disabled={isSavingDeliveredAt}
                className="w-[180px] rounded border border-indigo-300 px-1.5 py-0.5 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <div className="flex gap-1">
                <button
                  onClick={onSaveEditDeliveredAt}
                  disabled={isSavingDeliveredAt}
                  className="rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSavingDeliveredAt ? '...' : '저장'}
                </button>
                <button
                  onClick={onCancelEditDeliveredAt}
                  disabled={isSavingDeliveredAt}
                  className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-300 disabled:opacity-50"
                >
                  취소
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => onStartEditDeliveredAt(coupon)}
              className="group flex items-center gap-1 text-left"
              title="클릭하여 발송일시 수정"
            >
              <span>{formatDateTimeSec(coupon.gifticon?.deliveredAt)}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 shrink-0 text-gray-300 group-hover:text-indigo-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )
        ) : (
          '-'
        )}
      </td>

      {/* 도매가 */}
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-indigo-600">
        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
        coupon.gifticon?.priceWhole != null
          ? `${coupon.gifticon.priceWhole.toLocaleString()}원`
          : '-'}
      </td>

      {/* 매체 */}
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered ? (
          <div className="flex items-center gap-1.5">
            {channelEditMode &&
              (() => {
                const deliveredKey = coupon.gifticon?.deliveredAt
                  ? formatDateTimeSec(coupon.gifticon.deliveredAt)
                  : '__none__';
                const groupIds = deliveredGroupMap.get(deliveredKey) ?? [];
                const isFirst = groupIds[0] === coupon._id;
                const isGroupAllSelected =
                  groupIds.length > 0 &&
                  groupIds.every((id) => channelSelectedIds.has(id));

                return isFirst ? (
                  <input
                    type="checkbox"
                    checked={isGroupAllSelected}
                    onChange={() => onToggleChannelGroup(deliveredKey)}
                    title={`묶음 일괄선택 (${groupIds.length}건)`}
                    className="h-3.5 w-3.5 cursor-pointer rounded border-blue-400 text-blue-600 accent-blue-600"
                  />
                ) : (
                  <input
                    type="checkbox"
                    checked={channelSelectedIds.has(coupon._id)}
                    onChange={() => onToggleChannelOne(coupon._id)}
                    className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-blue-600 accent-blue-600"
                  />
                );
              })()}
            {coupon.gifticon?.deliveryChannel ? (
              <span
                className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                  DELIVERY_CHANNEL_CONFIG[coupon.gifticon.deliveryChannel]?.badgeClass
                }`}
              >
                {DELIVERY_CHANNEL_CONFIG[coupon.gifticon.deliveryChannel]?.label}
              </span>
            ) : (
              <span className="text-gray-300">-</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-300">-</span>
        )}
      </td>

      {/* 액션 */}
      <td className="whitespace-nowrap px-4 py-3 text-center">
        <div className="flex flex-col items-center gap-1">
          {coupon.isAdditional && (
            <button
              onClick={() => onDeleteAdditional(coupon)}
              disabled={isDeletingThis}
              className="rounded bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:opacity-50"
            >
              {isDeletingThis ? '처리중...' : '재발행폐기'}
            </button>
          )}
          {onIssueRefund &&
            !coupon.isAdditional &&
            !coupon.isRenewal &&
            !coupon.issued &&
            coupon.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
            coupon.redeemStatus !== GiftCouponRedeemStatus.Disposed &&
            coupon.redeemStatus !== GiftCouponRedeemStatus.Refunded && (
              <button
                onClick={() => onIssueRefund(coupon._id)}
                disabled={isIssuing}
                className="rounded bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
              >
                {isIssuing ? '...' : '환불'}
              </button>
            )}
          {onIssueRenewal &&
            !coupon.issued &&
            coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
            !coupon.isRenewed &&
            !coupon.isRenewal && (
              <button
                onClick={() => onIssueRenewal(coupon._id)}
                disabled={isIssuing}
                className="rounded bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-200 disabled:opacity-50"
              >
                {isIssuing ? '...' : '연장'}
              </button>
            )}
          {onCancelIssue && coupon.issued && !coupon.issued.requestedAt && (
            <button
              onClick={() => onCancelIssue(coupon._id)}
              disabled={isIssuing}
              className="rounded bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {isIssuing ? '...' : '링크취소'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
